// Tenant Detail/Edit — Features, Infrastructure, Locale tabs.
// Route: /platform/tenants/:orgId  (admin role only)
//
// One file holds all three tabs to keep the diff focused. Save sends only
// the affected subtree (PUT $set replaces the subdoc).

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import AppLayout from '../../components/AppLayout';
import useConfirm from '../../components/ConfirmDialog/useConfirm';
import { FEATURE_PRESETS, LOCALE_PRESETS } from './index';
import {
  PLATFORM_PRODUCTS, PLATFORM_CONTRACT_TERMS, productLabel,
  resolveProducts, resolveContractTerms,
} from '../../utils/productCatalog';
import { formatDate } from '../regureStyles';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';
axios.defaults.withCredentials = true;

const C = {
  burgundy:    '#800020',
  burgundyLt:  'rgba(128,0,32,0.08)',
  textPrimary: '#1e293b',
  textMuted:   '#64748b',
  bgCard:      '#f8fafc',
  border:      '#e2e8f0',
  green:       '#10b981',
  gray:        '#94a3b8',
};

// ─── Feature catalog (category → items) ─────────────────────
const FEATURE_CATALOG = [
  { category: 'Intelligence', items: [
    { key: 'dashboard',          label: 'Dashboard',                description: 'Main dashboard with analytics' },
    { key: 'documentProcessing', label: 'Document Processing',      description: 'Document AI classification and extraction' },
    { key: 'workflowAutomation', label: 'Workflow Automation',      description: 'Workflow engine' },
    { key: 'claimsAnalytics',    label: 'Claims Analytics',         description: 'Claims dashboard and breakdowns' },
    { key: 'riskSignals',        label: 'Risk Signals',             description: 'Risk signal monitoring' },
  ]},
  { category: 'Operations', items: [
    { key: 'clients',     label: 'Clients',     description: 'Client records, CSV import, document upload' },
    { key: 'claims',      label: 'Claims',      description: 'Claims management' },
    { key: 'policies',    label: 'Policies',    description: 'Policy portfolio view' },
    { key: 'offers',      label: 'Offers',      description: 'Offer / quote creation' },
    { key: 'invoicing',   label: 'Invoicing',   description: 'Invoice management' },
    { key: 'commissions', label: 'Commissions', description: 'Commission tracking' },
  ]},
  { category: 'Quoting Engines', indented: true, items: [
    { key: 'rateTables', label: 'Rate Tables', description: 'Rate table administration' },
    { key: 'pgma',       label: 'PGMA',        description: 'Daily Benefit for Doctors' },
    { key: 'rcPro',      label: 'RC Pro',      description: 'Professional Liability' },
    { key: 'pgm',        label: 'PGM',         description: 'Daily Benefit for Employees' },
  ]},
  { category: 'Document AI Capabilities', items: [
    { key: 'policyComparison', label: 'Policy Comparison', description: 'Policy comparison / renewal diff (Feature B)' },
  ]},
  { category: 'Communications', items: [
    { key: 'chat',  label: 'Chat',                description: 'Secure messaging' },
    { key: 'video', label: 'Video Conferencing',  description: 'Video meetings' },
    { key: 'eSign', label: 'E-Signatures',        description: 'Electronic signatures' },
  ]},
  { category: 'Compliance', items: [
    { key: 'auditTrail',       label: 'Audit Trail',       description: 'Audit trail' },
    { key: 'integrityMonitor', label: 'Integrity Monitor', description: 'Integrity monitoring' },
    { key: 'compliance',       label: 'Compliance Reports',description: 'Compliance reports' },
    { key: 'dsar',             label: 'DSAR Requests',     description: 'Data subject access requests' },
    { key: 'aiGovernance',     label: 'AI Governance',     description: 'AI usage and governance logs' },
  ]},
  { category: 'Administration', items: [
    { key: 'teamManagement', label: 'Team Management', description: 'User and team management' },
    { key: 'integrations',   label: 'Integrations',    description: 'Third-party integrations' },
  ]},
  { category: 'Advanced', items: [
    { key: 'brokers',       label: 'Brokers',         description: 'Broker management' },
    { key: 'riskCarriers',  label: 'Risk Carriers',   description: 'Risk carrier management' },
    { key: 'bordereaux',    label: 'Bordereaux',      description: 'Bordereaux reporting' },
    { key: 'apiKeys',       label: 'API Keys',        description: 'API key management' },
    { key: 'webhooks',      label: 'Webhooks',        description: 'Webhook configuration' },
    { key: 'dataResidency', label: 'Data Residency',  description: 'Data residency settings' },
  ]},
  // Reinsurance was gated by tenantConfig.features from the day it shipped,
  // but it had no row here — so the only way to turn a sub-module on was a
  // script against the config collection. Every flag the sidebar reads is
  // now switchable from this screen. Each is OFF for any tenant that has
  // not been given it; adding a row here does not enable anything.
  { category: 'Reinsurance', items: [
    { key: 'reinsuranceSubmissions',      label: 'Submissions',              description: 'Submission intake and the AI extraction pipeline' },
    { key: 'reinsurancePlacement',        label: 'Placement',                description: 'Placement board, capacity and the cross-deal queue' },
    { key: 'reinsuranceClosings',         label: 'Closings',                 description: 'Slips, debit and credit notes, FX rate table' },
    { key: 'reinsuranceClaimsValidation', label: 'Claims Validation',        description: 'Reinsurance claim capture and validation' },
    { key: 'reinsurancePortfolio',        label: 'Portfolio',                description: 'Portfolio concentration and exposure views' },
    { key: 'reinsuranceEndorsements',     label: 'Endorsements',             description: 'Mid-term changes against a bound deal' },
    { key: 'reinsurancePremiumDepth',     label: 'Premium Depth',            description: 'Minimum premium, instalments, NCB, profit commission, tax lines' },
    { key: 'reinsuranceSanctions',        label: 'Sanctions Screening',      description: 'Screening requests and attestations' },
    { key: 'reinsuranceDashboard',        label: 'Reinsurance Dashboard',    description: 'The module landing page at /reinsurance' },
    { key: 'reinsuranceTreaties',         label: 'Treaties & Facultative',   description: 'Treaty register, shares, and the facultative placement workspace' },
    { key: 'reinsuranceAccounting',       label: 'Bordereaux & Accounting',  description: 'Technical accounts, statements, settlements, overdue tracking' },
    { key: 'reinsuranceRecoveries',       label: 'Recoveries',               description: 'Reinsurance recoveries, aging and cash calls' },
    { key: 'reinsuranceCounterparties',   label: 'Counterparties & Compliance', description: 'Counterparty registry, ratings, KYC and local-market evidence' },
    { key: 'reinsuranceRenewals',         label: 'Renewals',                 description: 'Renewal pipeline, terms comparison and endorsement versions' },
  ]},
];

const AWS_REGIONS = [
  'us-east-1', 'us-east-2', 'us-west-1', 'us-west-2',
  'eu-central-1', 'eu-central-2', 'eu-west-1', 'eu-west-2',
  'ap-southeast-1', 'ap-southeast-2', 'ap-northeast-1',
];

const TIMEZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Bogota', 'America/Mexico_City', 'America/Sao_Paulo',
  'Europe/Zurich', 'Europe/London', 'Europe/Paris', 'Europe/Berlin',
  'Europe/Madrid', 'Europe/Lisbon', 'Europe/Rome', 'Europe/Brussels',
  'Europe/Amsterdam', 'Europe/Dublin', 'Europe/Vienna', 'Europe/Stockholm',
  'Europe/Warsaw', 'Asia/Dubai', 'Asia/Singapore', 'Asia/Tokyo',
  'Australia/Sydney', 'UTC',
];

// A tenant configured with a zone this list does not carry must still see
// its own value selected. Without this the <select> falls back to its first
// option, and the next save silently rewrites the tenant's timezone to
// America/New_York — the operator never chose it and never saw it change.
const timezoneOptions = (configured) =>
  (configured && !TIMEZONES.includes(configured)) ? [...TIMEZONES, configured] : TIMEZONES;

const Toggle = ({ checked, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    style={{
      width: 40, height: 22, borderRadius: 11,
      background: checked ? C.burgundy : '#cbd5e1',
      border: 'none', position: 'relative', cursor: 'pointer',
      transition: 'background 0.15s', flexShrink: 0,
    }}
    aria-pressed={checked}
  >
    <span style={{
      position: 'absolute', top: 2, left: checked ? 20 : 2,
      width: 18, height: 18, borderRadius: '50%', background: '#fff',
      transition: 'left 0.15s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    }} />
  </button>
);

// ──────────────────────────────────────────────────────────────
// Main component
// ──────────────────────────────────────────────────────────────
const TenantDetail = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { orgId } = useParams();
  const { confirm, ConfirmPortal } = useConfirm();
  const userLogin = useSelector(state => state.userLogin);
  const user = userLogin?.userInfo?.data?.user || userLogin?.userInfo?.data?.data;

  // Access control — admin (SaaS-god) only. See PlatformTenants/index.js.
  useEffect(() => {
    if (user && user.role !== 'admin') {
      history.push('/dashboard');
    }
  }, [user, history]);

  const [config,   setConfig]   = useState(null);
  const [original, setOriginal] = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [saving,   setSaving]   = useState(false);
  const [saveMsg,  setSaveMsg]  = useState(null);
  const [tab,      setTab]      = useState('features');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BASE_URL}/platform/tenants/${orgId}`);
      setConfig(data?.data || null);
      setOriginal(data?.data || null);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const save = async (subtree) => {
    setSaving(true);
    setSaveMsg(null);
    try {
      const { data } = await axios.put(`${BASE_URL}/platform/tenants/${orgId}`, subtree);
      setConfig(data?.data || config);
      setOriginal(data?.data || config);
      setSaveMsg(t('screens.tenantDetail.saved'));
      setTimeout(() => setSaveMsg(null), 2500);
    } catch (err) {
      setSaveMsg(t('screens.tenantDetail.saveFailed', { error: err.response?.data?.message || err.message }));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async () => {
    if (!config) return;
    if (config.isActive) {
      // Soft-delete
      const ok = await confirm({
        title: t('screens.tenantDetail.confirmDeactivateTitle', { defaultValue: 'Deactivate tenant?' }),
        message: t('screens.tenantDetail.confirmDeactivate', {
          defaultValue: `"${config.orgName}" will be marked inactive. Users will lose access. This is reversible via the Reactivate button.`,
          name: config.orgName,
        }),
        confirmText: t('screens.tenantDetail.deactivate', { defaultValue: 'Deactivate' }),
        variant: 'danger',
      });
      if (!ok) return;
      await axios.delete(`${BASE_URL}/platform/tenants/${orgId}`);
    } else {
      await axios.put(`${BASE_URL}/platform/tenants/${orgId}`, { isActive: true });
    }
    load();
  };

  if (loading) {
    return (
      <AppLayout>
        <div style={{ flex: 1, overflowY: 'auto', padding: 60, textAlign: 'center', color: C.textMuted }}>{t('screens.tenantDetail.loading')}</div>
      </AppLayout>
    );
  }

  if (error || !config) {
    return (
      <AppLayout>
        <div style={{ flex: 1, overflowY: 'auto', padding: 32 }}>
          <button onClick={() => history.push('/platform/tenants')} style={backBtn}>{t('screens.tenantDetail.back')}</button>
          <div style={{ marginTop: 20, background: '#fef2f2', color: '#991b1b', padding: 14, borderRadius: 8 }}>
            {error || t('screens.tenantDetail.notFound')}
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
       <div style={{ padding: '24px 32px', maxWidth: 1400, margin: '0 auto' }}>
        <button onClick={() => history.push('/platform/tenants')} style={backBtn}>{t('screens.tenantDetail.back')}</button>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginTop: 16, marginBottom: 8 }}>
          <div>
            <h1 style={{ fontSize: 26, color: C.textPrimary, margin: 0, fontWeight: 700 }}>
              {config.orgName || t('screens.tenantDetail.unnamed')}
            </h1>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
              <span style={{ fontFamily: 'monospace', fontSize: 12, color: C.textMuted, background: C.bgCard, padding: '3px 8px', borderRadius: 4 }}>
                {String(config.org)}
              </span>
              <span style={{
                padding: '3px 9px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                background: config.isActive ? 'rgba(16,185,129,0.12)' : 'rgba(148,163,184,0.15)',
                color:      config.isActive ? C.green : C.textMuted,
              }}>
                {config.isActive ? t('screens.tenantDetail.active') : t('screens.tenantDetail.inactive')}
              </span>
            </div>
          </div>
          <button onClick={toggleActive} style={btnSecondary}>
            {config.isActive ? t('screens.tenantDetail.deactivate') : t('screens.tenantDetail.activate')}
          </button>
        </div>

        {saveMsg && (
          <div style={{
            marginTop: 14,
            background: saveMsg.startsWith(t('screens.tenantDetail.saveFailed', { error: '' }).split(':')[0]) ? '#fef2f2' : 'rgba(16,185,129,0.1)',
            color:      saveMsg.startsWith(t('screens.tenantDetail.saveFailed', { error: '' }).split(':')[0]) ? '#991b1b' : '#047857',
            padding: '8px 12px', borderRadius: 6, fontSize: 13,
          }}>
            {saveMsg}
          </div>
        )}

        {/* Tabs */}
        <div style={{ marginTop: 24, borderBottom: `1px solid ${C.border}`, display: 'flex', gap: 4 }}>
          {[
            { id: 'features',       label: t('screens.tenantDetail.tabFeatures') },
            { id: 'ai',             label: t('screens.tenantDetail.tabAi',      { defaultValue: 'Reggie AI' }) },
            { id: 'products',       label: t('screens.tenantDetail.tabProducts') },
            { id: 'infrastructure', label: t('screens.tenantDetail.tabInfra') },
            { id: 'storage',        label: 'Storage' },
            { id: 'locale',         label: t('screens.tenantDetail.tabLocale') },
          ].map(opt => (
            <button
              key={opt.id}
              onClick={() => setTab(opt.id)}
              style={{
                padding: '10px 18px', fontSize: 13, fontWeight: 600,
                border: 'none', background: 'transparent', cursor: 'pointer',
                color: tab === opt.id ? C.burgundy : C.textMuted,
                borderBottom: tab === opt.id ? `2px solid ${C.burgundy}` : '2px solid transparent',
                marginBottom: -1,
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Tab body */}
        <div style={{ marginTop: 24 }}>
          {tab === 'features'       && <FeaturesTab        config={config} setConfig={setConfig} save={save} saving={saving} />}
          {tab === 'ai'             && <ReggieTab          orgId={orgId} orgName={config.orgName} />}
          {tab === 'products'       && <ProductsTab        config={config} setConfig={setConfig} save={save} saving={saving} />}
          {tab === 'infrastructure' && <InfrastructureTab  config={config} setConfig={setConfig} save={save} saving={saving} />}
          {tab === 'storage'        && <StorageTab         orgId={orgId} />}
          {tab === 'locale'         && <LocaleTab          config={config} setConfig={setConfig} save={save} saving={saving} />}
        </div>
       </div>
      </div>
      {ConfirmPortal}
    </AppLayout>
  );
};

// ──────────────────────────────────────────────────────────────
// Features tab
// ──────────────────────────────────────────────────────────────
function FeaturesTab({ config, setConfig, save, saving }) {
  const { t } = useTranslation();
  const features = config.features || {};

  const applyPreset = (key) => {
    setConfig({ ...config, features: { ...features, ...FEATURE_PRESETS[key].features } });
  };

  return (
    <div>
      {/* Presets */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, color: C.textMuted, textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5, alignSelf: 'center', marginRight: 4 }}>
          {t('screens.tenantDetail.quickPresets')}
        </span>
        {Object.entries(FEATURE_PRESETS).map(([key, p]) => (
          <button key={key} onClick={() => applyPreset(key)} style={presetBtn} title={p.description}>
            {p.label}
          </button>
        ))}
      </div>

      {FEATURE_CATALOG.map(group => (
        <div key={group.category} style={{ marginBottom: 28, marginLeft: group.indented ? 24 : 0 }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, color: C.textPrimary, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 12 }}>
            {group.category}
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 10 }}>
            {group.items.map(item => (
              <div key={item.key} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                padding: '12px 14px', background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8,
              }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.textPrimary }}>{item.label}</div>
                  <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>{item.description}</div>
                </div>
                <Toggle
                  checked={!!features[item.key]}
                  onChange={(v) => setConfig({ ...config, features: { ...features, [item.key]: v } })}
                />
              </div>
            ))}
          </div>
        </div>
      ))}

      <div style={{ marginTop: 24, paddingTop: 20, borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={() => save({ features })} disabled={saving} style={btnPrimary}>
          {saving ? t('screens.tenantDetail.saving') : t('screens.tenantDetail.saveChanges')}
        </button>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Products tab — the commercial vocabulary and terms this tenant sells
// on. Everything here is optional: leaving a list empty means "use the
// platform default", which is what every tenant provisioned before these
// fields existed already does. Nothing in this tab is specific to any
// one customer.
// ──────────────────────────────────────────────────────────────
// The locales the offer-document builder has string sets for. A locale is
// only usable once the carrier has also supplied its legal wording — the
// toggle records intent; services/assets/README-locales.md records what
// actually exists on disk.
const DOCUMENT_LOCALES = [
  { code: 'fr', labelKey: 'common.languages.fr' },
  { code: 'en', labelKey: 'common.languages.en' },
  { code: 'de', labelKey: 'common.languages.de' },
];

// Shown only when a toggle group is entirely unselected, which is the
// state that reads as "nothing enabled" and means the opposite.
const effectiveNote = {
  fontSize: 12,
  lineHeight: 1.5,
  padding: '8px 12px',
  marginBottom: 8,
  borderRadius: 8,
  background: '#fffbeb',
  border: '1px solid #fde68a',
  color: '#92400e',
};

function ProductsTab({ config, setConfig, save, saving }) {
  const { t } = useTranslation();
  const products    = Array.isArray(config.products) ? config.products : [];
  const terms       = Array.isArray(config.contractTermOptions) ? config.contractTermOptions.map(Number) : [];
  const specialties = Array.isArray(config.pgmaSpecialties) ? config.pgmaSpecialties : [];
  // What will ACTUALLY apply, computed by the same resolvers the offer
  // forms and the offer controllers call. An empty list means
  // "unconfigured, use the platform default" — not "none" — so three OFF
  // toggles display the minimum while the system applies the maximum.
  // Showing the resolved set removes that inversion without touching the
  // semantics of empty, which every tenant depends on.
  const effectiveProducts = resolveProducts(config);
  const effectiveTerms    = resolveContractTerms(config);

  const docLocales  = Array.isArray(config.offerDocuments?.documentLocales)
    ? config.offerDocuments.documentLocales.map(l => String(l || '').toLowerCase())
    : [];

  const toggleIn = (list, value) => (
    list.includes(value) ? list.filter(v => v !== value) : [...list, value]
  );

  return (
    <div>
      <h3 style={sectionH}>{t('screens.tenantDetail.productsHeader')}</h3>
      <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 12, lineHeight: 1.5 }}>
        {t('screens.tenantDetail.productsHelp')}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10, marginBottom: 8 }}>
        {PLATFORM_PRODUCTS.map(code => (
          <div key={code} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
            padding: '12px 14px', background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8,
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.textPrimary }}>
              {productLabel(t, code, null, 'long')}
            </div>
            <Toggle
              checked={products.includes(code)}
              onChange={() => setConfig({ ...config, products: toggleIn(products, code) })}
            />
          </div>
        ))}
      </div>

      {products.length === 0 && (
        <div style={effectiveNote}>
          {t('screens.tenantDetail.productsEffective', {
            list: effectiveProducts.map(c => productLabel(t, c, null, 'short')).join(', '),
          })}
        </div>
      )}

      <h3 style={sectionH}>{t('screens.tenantDetail.contractTermsHeader')}</h3>
      <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 12, lineHeight: 1.5 }}>
        {t('screens.tenantDetail.contractTermsHelp')}
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
        {PLATFORM_CONTRACT_TERMS.map(y => (
          <div key={y} style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 14px', background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8,
          }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: C.textPrimary }}>
              {t('offers.pgm.durationYears', { count: y })}
            </span>
            <Toggle
              checked={terms.includes(y)}
              onChange={() => setConfig({
                ...config,
                contractTermOptions: toggleIn(terms, y).sort((a, b) => a - b),
              })}
            />
          </div>
        ))}
      </div>

      {terms.length === 0 && (
        <div style={effectiveNote}>
          {t('screens.tenantDetail.contractTermsEffective', {
            list: effectiveTerms.map(y => t('offers.pgm.durationYears', { count: y })).join(', '),
          })}
        </div>
      )}

      <h3 style={sectionH}>{t('screens.tenantDetail.contractExpiryHeader')}</h3>
      <Row>
        <Field label={t('screens.tenantDetail.contractExpiryLabel')}>
          <input
            style={input}
            placeholder='12-31'
            value={config.defaultContractExpiry || ''}
            onChange={e => setConfig({ ...config, defaultContractExpiry: e.target.value })}
          />
          <div style={{ fontSize: 11, color: C.textMuted, marginTop: 6, lineHeight: 1.5 }}>
            {t('screens.tenantDetail.contractExpiryHelp')}
          </div>
        </Field>
      </Row>

      <h3 style={sectionH}>{t('screens.tenantDetail.docLocalesHeader')}</h3>
      {/* Which languages this tenant's offer documents actually EXIST in —
          not which languages the interface speaks. The generated PDFs embed
          carrier legal wording (CGA bodies, the contract-description FAQ,
          the PGMA health declaration) that only exists where the carrier has
          supplied and approved it. A request outside this list falls back to
          the first entry so the whole document comes out in one language;
          before this existed, asking for English produced English labels
          wrapped around French contract text. Empty = no restriction. */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {DOCUMENT_LOCALES.map(loc => (
          <div key={loc.code} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
            padding: '10px 14px', background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8,
          }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: C.textPrimary }}>
              {t(loc.labelKey, { defaultValue: loc.code.toUpperCase() })}
            </span>
            <Toggle
              checked={docLocales.includes(loc.code)}
              onChange={() => setConfig({
                ...config,
                offerDocuments: {
                  ...(config.offerDocuments || {}),
                  documentLocales: toggleIn(docLocales, loc.code),
                },
              })}
            />
          </div>
        ))}
      </div>
      <div style={{ fontSize: 11, color: C.textMuted, marginTop: 6, lineHeight: 1.5 }}>
        {t('screens.tenantDetail.docLocalesHelp')}
      </div>

      <h3 style={sectionH}>{t('screens.tenantDetail.pgmaSpecialtiesHeader')}</h3>
      <textarea
        style={{ ...input, minHeight: 110, resize: 'vertical' }}
        value={specialties.join(NEWLINE)}
        onChange={e => setConfig({
          ...config,
          // Split on newlines and keep the user's blank line while typing
          // by filtering only on save (below), not on every keystroke.
          pgmaSpecialties: e.target.value.split(NEWLINE_RE),
        })}
      />
      <div style={{ fontSize: 11, color: C.textMuted, marginTop: 6, lineHeight: 1.5 }}>
        {t('screens.tenantDetail.pgmaSpecialtiesHelp')}
      </div>

      <h3 style={sectionH}>{t('screens.tenantDetail.invoicingHeader')}</h3>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        padding: '12px 14px', background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8,
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.textPrimary }}>
            {t('screens.tenantDetail.autoInvoiceLabel')}
          </div>
          <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>
            {t('screens.tenantDetail.autoInvoiceHelp')}
          </div>
        </div>
        <Toggle
          checked={!!config.autoGenerateInvoice}
          onChange={(v) => setConfig({ ...config, autoGenerateInvoice: v })}
        />
      </div>

      <div style={{ marginTop: 24, paddingTop: 20, borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end' }}>
        <button
          onClick={() => save({
            products,
            contractTermOptions: terms,
            defaultContractExpiry: (config.defaultContractExpiry || '').trim(),
            // Drop blank lines only at save time so typing stays natural.
            pgmaSpecialties: specialties.map(v => v.trim()).filter(Boolean),
            autoGenerateInvoice: !!config.autoGenerateInvoice,
            // DOT PATH, deliberately. The endpoint does `$set: req.body`, so
            // sending `offerDocuments: { documentLocales }` would REPLACE the
            // whole subdocument and silently wipe the tenant's disclaimer
            // text, correspondence/broker/coverholder addresses, CGA editions
            // and master-contract numbers — every setting the generated
            // documents depend on. A dotted key updates only this field and
            // leaves its siblings untouched.
            'offerDocuments.documentLocales': docLocales,
          })}
          disabled={saving}
          style={btnPrimary}
        >
          {saving ? t('screens.tenantDetail.saving') : t('screens.tenantDetail.saveChanges')}
        </button>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Infrastructure tab
// ──────────────────────────────────────────────────────────────
// Reggie AI tab — toggles the tenant's aiEnabled flag (which lives on
// the org's User doc, NOT on the TenantConfig doc — see
// authController.js:745 which mirrors currentClient.aiEnabled onto the
// login response's data.user). Uses the existing
// PATCH /orgs/:orgId/reggie-settings endpoint.
//
// Pre-2026-08-07 the only UI surface for this toggle was on the
// SuperAdmin dashboard which is only reachable on the platform-admin
// deployment. Regional deploys (ch.getregure.com) had to run direct-DB
// scripts to flip aiEnabled — see reference-kms-health-probe (KMS
// diagnosis) followup notes.
function ReggieTab({ orgId, orgName }) {
  const { t } = useTranslation();
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState(null);
  const [aiEnabled, setAiEnabled] = useState(false);
  const [aiEnabledUntil, setAiEnabledUntil] = useState(null);
  const [enableTrial, setEnableTrial] = useState(false);
  const [ok, setOk] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // Dedicated endpoint that reads from the User model (same place
        // updateReggieSettings writes). GET /orgs/:id returns the Org
        // model which doesn't carry aiEnabled and would render the tab
        // as "disabled" for tenants where it's actually on.
        const resp = await axios.get(`${BASE_URL}/orgs/${orgId}/reggie-settings`);
        const org = resp.data?.data?.org || {};
        if (!cancelled) {
          setAiEnabled(Boolean(org.aiEnabled));
          setAiEnabledUntil(org.aiEnabledUntil || null);
        }
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.message || err.message || 'Failed to load Reggie settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orgId]);

  const handleSave = async (nextEnabled) => {
    setSaving(true);
    setError(null);
    setOk(false);
    try {
      const resp = await axios.patch(`${BASE_URL}/orgs/${orgId}/reggie-settings`, {
        aiEnabled: nextEnabled,
        enableTrial: nextEnabled ? enableTrial : false,
      });
      const org = resp.data?.data?.org || {};
      setAiEnabled(Boolean(org.aiEnabled));
      setAiEnabledUntil(org.aiEnabledUntil || null);
      setOk(true);
      setTimeout(() => setOk(false), 2500);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to update Reggie settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ padding: 24, color: C.muted, fontSize: 13 }}>{t('common.loading', { defaultValue: 'Loading…' })}</div>;
  }

  const expiresLabel = aiEnabledUntil
    ? formatDate(aiEnabledUntil)
    : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ fontSize: 14, color: C.textMuted }}>
        {t('screens.tenantDetail.reggie.intro', {
          defaultValue: `Reggie is Regure's AI claims assistant. When enabled for ${orgName || 'this tenant'}, adjusters see an "Ask Reggie" button on claim detail pages and can chat with the tenant-scoped assistant.`,
          orgName: orgName || 'this tenant',
        })}
      </div>

      <div
        style={{
          padding: '18px 20px',
          background: aiEnabled ? '#f0fdf4' : '#f8fafc',
          border: `1px solid ${aiEnabled ? '#86efac' : C.border}`,
          borderRadius: 12,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div
            style={{
              width: 46, height: 46, borderRadius: 12,
              background: `linear-gradient(135deg, ${C.burgundy}, ${C.burgundy2 || '#722f37'})`,
              color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 20, fontWeight: 800,
            }}
          >R</div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>
              {t('screens.tenantDetail.reggie.title', { defaultValue: 'Reggie AI Assistant' })}
            </div>
            <div style={{ fontSize: 12, color: aiEnabled ? '#166534' : C.muted, marginTop: 3 }}>
              {aiEnabled
                ? (expiresLabel
                    ? t('screens.tenantDetail.reggie.enabledUntil', { defaultValue: `Enabled — trial expires ${expiresLabel}`, date: expiresLabel })
                    : t('screens.tenantDetail.reggie.enabled', { defaultValue: 'Enabled — no expiry' }))
                : t('screens.tenantDetail.reggie.disabled', { defaultValue: 'Disabled for this tenant' })}
            </div>
          </div>
        </div>
        <button
          type='button'
          onClick={() => handleSave(!aiEnabled)}
          disabled={saving}
          style={{
            padding: '10px 20px',
            background: aiEnabled ? '#fff' : `linear-gradient(135deg, ${C.burgundy}, ${C.burgundy2 || '#722f37'})`,
            color: aiEnabled ? '#991b1b' : '#fff',
            border: aiEnabled ? '1px solid #fca5a5' : 'none',
            borderRadius: 8,
            fontSize: 13, fontWeight: 700,
            cursor: saving ? 'wait' : 'pointer',
            opacity: saving ? 0.6 : 1,
          }}
        >
          {saving
            ? '…'
            : aiEnabled
              ? t('screens.tenantDetail.reggie.disable', { defaultValue: 'Disable' })
              : t('screens.tenantDetail.reggie.enable',  { defaultValue: 'Enable' })}
        </button>
      </div>

      {!aiEnabled && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: C.textMuted, cursor: 'pointer' }}>
          <input
            type='checkbox'
            checked={enableTrial}
            onChange={e => setEnableTrial(e.target.checked)}
            disabled={saving}
          />
          {t('screens.tenantDetail.reggie.trialCheckbox', { defaultValue: 'Set a 30-day trial expiry (aiEnabledUntil = today + 30 days)' })}
        </label>
      )}

      {error && (
        <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, fontSize: 13, color: '#991b1b' }}>
          {error}
        </div>
      )}
      {ok && (
        <div style={{ padding: '10px 14px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, fontSize: 13, color: '#166534' }}>
          {t('screens.tenantDetail.reggie.saveOk', { defaultValue: 'Reggie settings updated.' })}
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
function InfrastructureTab({ config, setConfig, save, saving }) {
  const { t } = useTranslation();
  const infra    = config.infrastructure || {};
  const s3       = infra.s3 || {};
  const quotas   = config.quotas || {};

  const setInfra = (patch) => setConfig({ ...config, infrastructure: { ...infra, ...patch } });
  const setS3    = (patch) => setInfra({ s3: { ...s3, ...patch } });
  const setQuotas = (patch) => setConfig({ ...config, quotas: { ...quotas, ...patch } });

  const envVar = `MONGO_URI_${(infra.mongoConnectionName || 'default').replace(/([A-Z])/g, '_$1').toUpperCase()}`;

  return (
    <div style={{ maxWidth: 720 }}>
      <h3 style={sectionH}>{t('screens.tenantDetail.quotasHeader')}</h3>
      <Field label={t('screens.tenantDetail.maxUsersLabel')}>
        <input
          type="number"
          min="0"
          value={quotas.maxUsers ?? ''}
          onChange={e => setQuotas({ maxUsers: e.target.value === '' ? null : Number(e.target.value) })}
          placeholder={t('screens.tenantDetail.maxUsersPh')}
          style={input}
        />
      </Field>
      {/* Commercial storage terms. Owned by SuperAdmin and set here; a
          Tenant Admin sees them read-only in their own Settings and has no
          write path to them (services/tenantSettings.js allowlist). */}
      <Field label={t('screens.tenantDetail.maxStorageLabel')}>
        <input
          type="number"
          min="0"
          value={quotas.maxStorageGB ?? ''}
          onChange={e => setQuotas({ maxStorageGB: e.target.value === '' ? null : Number(e.target.value) })}
          placeholder={t('screens.tenantDetail.unlimitedPh')}
          style={input}
        />
      </Field>
      <Field label={t('screens.tenantDetail.maxFileSizeLabel')}>
        <input
          type="number"
          min="0"
          value={quotas.maxFileSizeMB ?? ''}
          onChange={e => setQuotas({ maxFileSizeMB: e.target.value === '' ? null : Number(e.target.value) })}
          placeholder={t('screens.tenantDetail.unlimitedPh')}
          style={input}
        />
      </Field>
      <div style={{ display: 'flex', gap: 16 }}>
        <Field label={t('screens.tenantDetail.warnAtLabel')}>
          <input
            type="number" min="1" max="99"
            value={quotas.warnAtPercent ?? 75}
            onChange={e => setQuotas({ warnAtPercent: Number(e.target.value) })}
            style={input}
          />
        </Field>
        <Field label={t('screens.tenantDetail.criticalAtLabel')}>
          <input
            type="number" min="1" max="100"
            value={quotas.criticalAtPercent ?? 90}
            onChange={e => setQuotas({ criticalAtPercent: Number(e.target.value) })}
            style={input}
          />
        </Field>
      </div>
      <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 24 }}>
        {t('screens.tenantDetail.quotasHint')}
      </div>

      <h3 style={sectionH}>{t('screens.tenantDetail.mongoHeader')}</h3>
      <Field label={t('screens.tenantDetail.mongoConnLabel')}>
        <input
          value={infra.mongoConnectionName || ''}
          onChange={e => setInfra({ mongoConnectionName: e.target.value })}
          placeholder={t('screens.tenantDetail.mongoConnPh')}
          style={input}
        />
      </Field>
      <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 24 }}>
        {t('screens.tenantDetail.mongoResolves')} <code style={{ background: C.bgCard, padding: '2px 6px', borderRadius: 3 }}>{envVar}</code>
      </div>

      <h3 style={sectionH}>{t('screens.tenantDetail.s3Header')}</h3>
      <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 12, padding: 10, background: 'rgba(196,162,101,0.08)', border: '1px solid rgba(196,162,101,0.25)', borderRadius: 6 }}>
        {t('screens.tenantDetail.s3Warning')}
      </div>
      <Field label={t('screens.tenantDetail.bucketLabel')}>
        <input
          value={s3.bucket || ''}
          onChange={e => setS3({ bucket: e.target.value })}
          placeholder={t('screens.tenantDetail.bucketPh')}
          style={input}
        />
      </Field>
      <Field label={t('screens.tenantDetail.regionLabel')}>
        <select value={s3.region || ''} onChange={e => setS3({ region: e.target.value || null })} style={input}>
          <option value="">{t('screens.tenantDetail.regionGlobal')}</option>
          {AWS_REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
      </Field>

      <div style={{ marginTop: 24, paddingTop: 20, borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={() => save({ infrastructure: { ...infra, s3 }, quotas })} disabled={saving} style={btnPrimary}>
          {saving ? t('screens.tenantDetail.saving') : t('screens.tenantDetail.saveChanges')}
        </button>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Storage tab — per-tenant S3 target with "Test storage" probe
// ──────────────────────────────────────────────────────────────
//
// Fields are held in local state (not on the wider config object)
// because Storage has its own dedicated endpoint pair (GET/PATCH
// /platform/tenants/:id/storage) and its own audit-log action —
// piggybacking on the shared PUT would blur the audit trail.
function StorageTab({ orgId }) {
  const [state,    setState]    = useState(null);   // { configured, resolved, platformDefault }
  const [draft,    setDraft]    = useState({ bucket: '', region: '', keyPrefix: '' });
  const [loading,  setLoading]  = useState(true);
  const [loadErr,  setLoadErr]  = useState(null);
  const [saving,   setSaving]   = useState(false);
  const [saveMsg,  setSaveMsg]  = useState(null);
  const [testing,  setTesting]  = useState(false);
  const [testRes,  setTestRes]  = useState(null);   // { ok, phase, bucket, region, source, error, ... }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BASE_URL}/platform/tenants/${orgId}/storage`);
      setState(data?.data || null);
      setDraft(data?.data?.configured || { bucket: '', region: '', keyPrefix: '' });
      setLoadErr(null);
    } catch (err) {
      setLoadErr(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const onSave = async () => {
    setSaving(true);
    setSaveMsg(null);
    try {
      const { data } = await axios.patch(`${BASE_URL}/platform/tenants/${orgId}/storage`, draft);
      setState(data?.data);
      setDraft(data?.data?.configured || draft);
      setSaveMsg({ kind: 'ok', text: 'Saved.' });
      setTestRes(null);   // stale after any change
      setTimeout(() => setSaveMsg(null), 2500);
    } catch (err) {
      setSaveMsg({ kind: 'err', text: err.response?.data?.message || err.message });
    } finally {
      setSaving(false);
    }
  };

  const onTest = async () => {
    setTesting(true);
    setTestRes(null);
    try {
      const { data } = await axios.post(`${BASE_URL}/platform/tenants/${orgId}/storage/test`);
      setTestRes(data?.data || { ok: false, error: 'No response body' });
    } catch (err) {
      setTestRes({ ok: false, error: err.response?.data?.message || err.message });
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <div style={{ padding: 20, color: C.textMuted }}>Loading storage config…</div>;
  if (loadErr) return <div style={{ padding: 14, background: '#fef2f2', color: '#991b1b', borderRadius: 8 }}>{loadErr}</div>;
  if (!state)  return <div style={{ padding: 14, color: C.textMuted }}>No storage config.</div>;

  const isTenantSource = state.resolved.source === 'tenant';

  return (
    <div style={{ maxWidth: 720 }}>
      {/* Resolved-values pill */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        padding: 12, marginBottom: 20,
        background: isTenantSource ? 'rgba(16,185,129,0.08)' : 'rgba(148,163,184,0.12)',
        border: `1px solid ${isTenantSource ? 'rgba(16,185,129,0.35)' : C.border}`,
        borderRadius: 8,
      }}>
        <span style={{
          fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase',
          padding: '3px 8px', borderRadius: 4,
          background: isTenantSource ? C.green : C.gray,
          color: '#fff',
        }}>
          {isTenantSource ? 'source: tenant' : 'source: default'}
        </span>
        <span style={{ fontSize: 13, color: C.textPrimary }}>
          Resolves to{' '}
          <code style={{ background: '#fff', padding: '2px 6px', borderRadius: 3 }}>{state.resolved.bucket || '—'}</code>
          {' in '}
          <code style={{ background: '#fff', padding: '2px 6px', borderRadius: 3 }}>{state.resolved.region || '—'}</code>
          {state.resolved.keyPrefix ? <> · prefix <code style={{ background: '#fff', padding: '2px 6px', borderRadius: 3 }}>{state.resolved.keyPrefix}</code></> : null}
        </span>
      </div>

      <h3 style={sectionH}>Tenant-configured</h3>
      <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 12 }}>
        Leave a field empty to fall back to the platform default for that field
        only. A tenant can override just the bucket and keep the default region.
      </div>
      <Row>
        <Field label="Bucket">
          <input
            value={draft.bucket}
            onChange={e => setDraft({ ...draft, bucket: e.target.value })}
            placeholder={`Default: ${state.platformDefault.bucket || '(unset)'}`}
            style={input}
          />
        </Field>
        <Field label="Region">
          <select value={draft.region} onChange={e => setDraft({ ...draft, region: e.target.value })} style={input}>
            <option value="">Platform default ({state.platformDefault.region || 'unset'})</option>
            {AWS_REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>
      </Row>
      <Field label="Key prefix (optional)">
        <input
          value={draft.keyPrefix}
          onChange={e => setDraft({ ...draft, keyPrefix: e.target.value })}
          placeholder="e.g. argos-ch (adds path in front of the per-tenant orgId prefix)"
          style={input}
        />
      </Field>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 20, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
        <button onClick={onTest} disabled={testing || saving} style={btnSecondary}>
          {testing ? 'Testing…' : 'Test storage'}
        </button>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          {saveMsg && (
            <span style={{
              fontSize: 12,
              color: saveMsg.kind === 'ok' ? '#047857' : '#991b1b',
            }}>{saveMsg.text}</span>
          )}
          <button onClick={onSave} disabled={saving} style={btnPrimary}>
            {saving ? 'Saving…' : 'Save storage config'}
          </button>
        </div>
      </div>

      {testRes && (
        <div style={{
          marginTop: 20, padding: 14, borderRadius: 8,
          background: testRes.ok ? 'rgba(16,185,129,0.08)' : '#fef2f2',
          border: `1px solid ${testRes.ok ? 'rgba(16,185,129,0.35)' : '#fecaca'}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{
              fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6,
              padding: '3px 8px', borderRadius: 4,
              background: testRes.ok ? C.green : '#dc2626',
              color: '#fff',
            }}>
              {testRes.ok ? 'success' : 'failure'}
            </span>
            <span style={{ fontSize: 13, fontWeight: 600, color: C.textPrimary }}>
              {testRes.ok
                ? `Put · Get · Delete OK on ${testRes.bucket} / ${testRes.region}`
                : `Failed at ${testRes.phase || 'setup'}: ${testRes.error || 'unknown'}`}
            </span>
          </div>
          {testRes.ok && (
            <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4 }}>
              Probed key: <code style={{ background: '#fff', padding: '1px 5px', borderRadius: 3 }}>{testRes.keyProbed}</code>
            </div>
          )}
          {!testRes.ok && testRes.errorCode && (
            <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4 }}>
              AWS error code: <code style={{ background: '#fff', padding: '1px 5px', borderRadius: 3 }}>{testRes.errorCode}</code>
            </div>
          )}
        </div>
      )}

      {/* Platform-default reference */}
      <div style={{ marginTop: 24, padding: 12, background: C.bgCard, borderRadius: 6, fontSize: 12, color: C.textMuted }}>
        <strong style={{ color: C.textPrimary }}>Platform default:</strong>{' '}
        bucket <code style={{ background: '#fff', padding: '1px 5px', borderRadius: 3 }}>{state.platformDefault.bucket || '(unset)'}</code>,
        region <code style={{ background: '#fff', padding: '1px 5px', borderRadius: 3 }}>{state.platformDefault.region || '(unset)'}</code>.
        Tenants with no override land here.
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Locale tab
// ──────────────────────────────────────────────────────────────
function LocaleTab({ config, setConfig, save, saving }) {
  const { t } = useTranslation();
  const locale = config.locale || {};
  const setL   = (patch) => setConfig({ ...config, locale: { ...locale, ...patch } });
  const setN   = (patch) => setL({ numberFormat: { ...(locale.numberFormat || {}), ...patch } });

  const applyPreset = (key) => setConfig({ ...config, locale: LOCALE_PRESETS[key] });

  const preview = useMemo(() => {
    const date = new Date(2026, 5, 30);
    const dateFormatted = formatDateByPattern(date, locale.dateFormat);
    const amountFormatted = formatNumberByLocale(18500.5, locale, true);
    const numberFormatted = formatNumberByLocale(1234567.89, locale, false);
    return {
      date:    dateFormatted,
      amount:  `${locale.currencySymbol || ''} ${amountFormatted}`.trim(),
      number:  numberFormatted,
      state:   locale.stateLabel || '—',
      postal:  locale.postalCodeLabel || '—',
    };
  }, [locale]);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 32 }}>
      <div>
        <div style={{ marginBottom: 16 }}>
          <button onClick={() => applyPreset('US')} style={presetBtn}>{t('screens.tenantDetail.usPreset')}</button>
          <button onClick={() => applyPreset('CH')} style={{ ...presetBtn, marginLeft: 8 }}>{t('screens.tenantDetail.swissPreset')}</button>
        </div>

        <h3 style={sectionH}>{t('screens.tenantDetail.langRegion')}</h3>
        <Row>
          <Field label={t('screens.tenantDetail.language')}>
            <select value={locale.language || 'en'} onChange={e => setL({ language: e.target.value })} style={input}>
              <option value="en">{t('screens.tenantDetail.langEn')}</option>
              <option value="fr">{t('screens.tenantDetail.langFr')}</option>
              <option value="de">{t('screens.tenantDetail.langDe')}</option>
            </select>
          </Field>
          <Field label={t('screens.tenantDetail.countryIso')}>
            <input value={locale.country || ''} onChange={e => setL({ country: e.target.value.toUpperCase() })} style={input} placeholder={t('screens.tenantDetail.countryPh')} />
          </Field>
        </Row>

        <h3 style={sectionH}>{t('screens.tenantDetail.currency')}</h3>
        <Row>
          <Field label={t('screens.tenantDetail.currencyCode')}>
            <input value={locale.currency || ''} onChange={e => setL({ currency: e.target.value.toUpperCase() })} style={input} placeholder={t('screens.tenantDetail.currencyCodePh')} />
          </Field>
          <Field label={t('screens.tenantDetail.currencySymbol')}>
            <input value={locale.currencySymbol || ''} onChange={e => setL({ currencySymbol: e.target.value })} style={input} placeholder={t('screens.tenantDetail.currencySymbolPh')} />
          </Field>
        </Row>

        <h3 style={sectionH}>{t('screens.tenantDetail.dateTime')}</h3>
        <Row>
          <Field label={t('screens.tenantDetail.dateFormat')}>
            <select value={locale.dateFormat || 'MM/DD/YYYY'} onChange={e => setL({ dateFormat: e.target.value })} style={input}>
              {/* Describe the ordering, not a nationality. A format is not
                  owned by a country, and labelling DD/MM/YYYY "(EU)" tells a
                  Colombian or Australian tenant they picked Europe. */}
              <option value="MM/DD/YYYY">MM/DD/YYYY (Month/Day/Year)</option>
              <option value="DD.MM.YYYY">DD.MM.YYYY (Day.Month.Year)</option>
              <option value="DD/MM/YYYY">DD/MM/YYYY (Day/Month/Year)</option>
              <option value="YYYY-MM-DD">YYYY-MM-DD (ISO 8601)</option>
            </select>
          </Field>
          <Field label={t('screens.tenantDetail.timezone')}>
            <select value={locale.timezone || ''} onChange={e => setL({ timezone: e.target.value })} style={input}>
              {!locale.timezone && <option value=''>—</option>}
              {timezoneOptions(locale.timezone).map(tz => <option key={tz} value={tz}>{tz}</option>)}
            </select>
          </Field>
        </Row>

        <h3 style={sectionH}>{t('screens.tenantDetail.numberFormat')}</h3>
        <Row>
          <Field label={t('screens.tenantDetail.thousandsSep')}>
            <select value={locale.numberFormat?.thousandsSeparator || ','} onChange={e => setN({ thousandsSeparator: e.target.value })} style={input}>
              <option value=",">, (comma)</option>
              <option value="'">' (apostrophe)</option>
              <option value=".">. (period)</option>
              <option value=" "> (space)</option>
            </select>
          </Field>
          <Field label={t('screens.tenantDetail.decimalSep')}>
            <select value={locale.numberFormat?.decimalSeparator || '.'} onChange={e => setN({ decimalSeparator: e.target.value })} style={input}>
              <option value=".">. (period)</option>
              <option value=",">, (comma)</option>
            </select>
          </Field>
        </Row>

        <h3 style={sectionH}>{t('screens.tenantDetail.addressLabels')}</h3>
        <Row>
          <Field label={t('screens.tenantDetail.stateLabel')}>
            <input value={locale.stateLabel || ''} onChange={e => setL({ stateLabel: e.target.value })} style={input} placeholder={t('screens.tenantDetail.stateLabelPh')} />
          </Field>
          <Field label={t('screens.tenantDetail.postalLabel')}>
            <input value={locale.postalCodeLabel || ''} onChange={e => setL({ postalCodeLabel: e.target.value })} style={input} placeholder={t('screens.tenantDetail.postalLabelPh')} />
          </Field>
        </Row>
        <Field label={t('screens.tenantDetail.phoneFormat')}>
          <select value={locale.phoneFormat || 'US'} onChange={e => setL({ phoneFormat: e.target.value })} style={input}>
            <option value="US">{t('screens.tenantDetail.phoneUS')}</option>
            <option value="CH">{t('screens.tenantDetail.phoneCH')}</option>
            <option value="International">{t('screens.tenantDetail.phoneIntl')}</option>
          </select>
        </Field>

        <div style={{ marginTop: 24, paddingTop: 20, borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={() => save({ locale })} disabled={saving} style={btnPrimary}>
            {saving ? t('screens.tenantDetail.saving') : t('screens.tenantDetail.saveChanges')}
          </button>
        </div>
      </div>

      {/* Live preview */}
      <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 10, padding: 18, alignSelf: 'flex-start', position: 'sticky', top: 20 }}>
        <h4 style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.8, margin: 0, marginBottom: 14 }}>
          {t('screens.tenantDetail.livePreview')}
        </h4>
        <PreviewRow k={t('screens.tenantDetail.sampleDate')}    v={preview.date}   />
        <PreviewRow k={t('screens.tenantDetail.sampleAmount')}  v={preview.amount} />
        <PreviewRow k={t('screens.tenantDetail.sampleNumber')}  v={preview.number} />
        <PreviewRow k={t('screens.tenantDetail.addressLabel')}  v={preview.state}  />
        <PreviewRow k={t('screens.tenantDetail.postalLabelShort')}   v={preview.postal} />
      </div>
    </div>
  );
}

function formatDateByPattern(date, pattern) {
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const yyyy = String(date.getFullYear());
  switch (pattern) {
    case 'DD.MM.YYYY': return `${dd}.${mm}.${yyyy}`;
    case 'DD/MM/YYYY': return `${dd}/${mm}/${yyyy}`;
    case 'YYYY-MM-DD': return `${yyyy}-${mm}-${dd}`;
    case 'MM/DD/YYYY':
    default:           return `${mm}/${dd}/${yyyy}`;
  }
}

function formatNumberByLocale(n, locale, withDecimals) {
  const ts = locale.numberFormat?.thousandsSeparator ?? ',';
  const ds = locale.numberFormat?.decimalSeparator   ?? '.';
  const fixed = withDecimals ? n.toFixed(2) : String(Math.round(n * 100) / 100);
  const [int, dec] = fixed.split('.');
  const withSep = int.replace(/\B(?=(\d{3})+(?!\d))/g, ts);
  return dec ? `${withSep}${ds}${dec}` : withSep;
}

function PreviewRow({ k, v }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '8px 0', borderBottom: `1px dashed ${C.border}`, fontSize: 13 }}>
      <span style={{ color: C.textMuted }}>{k}</span>
      <span style={{ fontWeight: 600, color: C.textPrimary, fontFamily: 'monospace' }}>{v}</span>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 14, flex: 1 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{label}</label>
      {children}
    </div>
  );
}

function Row({ children }) {
  return <div style={{ display: 'flex', gap: 16 }}>{children}</div>;
}

// Newline helpers for the multi-line specialties editor, built from
// char codes so no source rewrite can mangle the escape sequences.
const NEWLINE = String.fromCharCode(10);
const NEWLINE_RE = new RegExp(String.fromCharCode(13) + '?' + String.fromCharCode(10));

// ─── shared style tokens ───
const sectionH = { fontSize: 13, fontWeight: 700, color: C.textPrimary, textTransform: 'uppercase', letterSpacing: 0.8, marginTop: 16, marginBottom: 12 };
const input = {
  width: '100%', padding: '9px 11px', fontSize: 13, color: C.textPrimary,
  border: `1px solid ${C.border}`, borderRadius: 7, outline: 'none', boxSizing: 'border-box',
  fontFamily: 'inherit', background: '#fff',
};
const btnPrimary = {
  padding: '10px 22px', background: C.burgundy, color: '#fff',
  border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
};
const btnSecondary = {
  padding: '8px 16px', background: '#fff', color: C.textPrimary,
  border: `1px solid ${C.border}`, borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer',
};
const backBtn = {
  background: 'transparent', border: 'none', color: C.burgundy,
  fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0,
};
const presetBtn = {
  padding: '6px 12px', fontSize: 12, fontWeight: 600, color: C.burgundy,
  background: C.burgundyLt, border: 'none', borderRadius: 6, cursor: 'pointer',
};

export default TenantDetail;
